import React, { useContext, useMemo, useEffect, memo } from 'react';
import { Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import AuthContext from "flexbiz-core/components/auth/Context";
import { counterQueue } from './counterQueue';
import {
  quickFilterKind, quickFilterOptions, quickFilterIsActive, quickFilterIsEmpty,
  toggleQuickFilterValue, quickFilterAutoPick, quickFilterCondition, combineConditions,
} from './quickFilterRules';
import { quickFilterValue } from './listinfoProps';

const IOS_BLUE = '#007aff';
const NO_ITEMS = [];   // hằng: `= []` trong destructuring sinh mảng MỚI mỗi lần vẽ -> useMemo vô hiệu

// Nạp danh mục cho hàng lọc. `api:"trangthai"` đọc `trangthaiapp` (trạng thái riêng của công ty) trước,
// không có mới lùi về `trangthai` — đúng như FilterQuick.js.
async function loadQuickFilterItems(context, userToken, config) {
  const get = (api) => context.apis.asyncGetList(userToken, api, {
    condition: config.apiCondition || {}, limit: 500, sort: config.apiSort,
  }).then(res => res?.data || res || []);
  if (config.api === 'trangthai') {
    const own = await get('trangthaiapp');
    if (Array.isArray(own) && own.length) return own;
  }
  return get(config.api);
}

const chipSx = (isActive, color) => ({
  fontWeight: isActive ? 'bold' : '500',
  bgcolor: isActive ? IOS_BLUE : 'white',
  color: isActive ? 'white' : (color || 'text.primary'),
  border: isActive ? 'none' : '1px solid #e5e5ea',
  '&:hover': { bgcolor: isActive ? '#005bb5' : '#f4f4f5' },
});

/**
 * Số bản ghi trên một chip (← `counterCode` của bộ cũ). Đếm trên CHÍNH API của danh sách, với điều
 * kiện đang áp của các bộ lọc KHÁC + giá trị của chip này. Đi qua hàng đợi chung (tối đa 5 lượt song
 * song) để một hàng hai chục chip không bắn hai chục request cùng lúc.
 */
function ChipCount({ apiCode, headers, condition }) {
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');
  const { data } = useQuery({
    queryKey: [context.config?.id_app, apiCode, 'quickFilter_count', condition],
    queryFn: () => counterQueue.enqueue(async () => {
      const rs = await context.apis.asyncGetList(userToken, apiCode, { condition, count: true, headers });
      return rs?.rows_number ?? rs?.pagination?.total ?? 0;
    }),
    enabled: !!apiCode && !!userToken,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
  if (data === undefined) return null;
  return <span style={{ marginLeft: 4, opacity: 0.85 }}>({new Intl.NumberFormat('vi-VN').format(data || 0)})</span>;
}

/**
 * MỘT hàng lọc nhanh. DataListManager vẽ bao nhiêu hàng tuỳ `quickFilterConfigs`.
 * @prop countApiCode / countHeaders / countBaseCondition  chỉ cần khi `config.showCount`
 */
const DataListQuickFilter = memo(({ config, activeValue, onChange, countApiCode, countHeaders, countBaseCondition }) => {
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');
  const kind = quickFilterKind(config);

  const { data: apiItems = NO_ITEMS, isLoading: loading } = useQuery({
    // id_app nằm trong khoá: đổi công ty thì danh mục (kho, trạng thái…) cũng đổi.
    queryKey: ['quickFilter_options', context.config?.id_app, config?.api, config?.apiCondition, config?.apiSort],
    queryFn: () => loadQuickFilterItems(context, userToken, config),
    // BẢN TRƯỚC: chỉ nạp khi "độ dài options bằng 0" — cấu hình KHÔNG khai `options` thì
    // `undefined == 0` là false, danh mục không bao giờ được nạp và cả hàng lọc biến mất.
    enabled: kind === 'api' && !!userToken,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const options = useMemo(() => quickFilterOptions(config || {}, apiItems), [config, apiItems]);

  // `selectAll:false` -> không có chip "tất cả", chọn sẵn chip đầu khi danh mục về.
  useEffect(() => {
    const pick = quickFilterAutoPick(config, activeValue, options);
    if (pick !== undefined) onChange(pick);
  }, [config, activeValue, options, onChange]);

  if (!config || kind === 'none') return null;

  const rowSx = {
    display: 'flex', alignItems: 'center', overflowX: 'auto', gap: 1, px: 2, pb: 1, pt: 1,
    bgcolor: '#f2f2f7', '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none',
  };
  const title = config.title
    ? <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600, mr: 0.5 }}>{config.title}</Typography>
    : null;

  // ---- HÀNG LỌC THEO NGÀY (api: "Date" | "DateTime") ----
  if (kind === 'date') {
    const isDT = config.api === 'DateTime';
    return (
      <Box sx={rowSx}>
        {title}
        <TextField
          type={isDT ? 'datetime-local' : 'date'} size="small" variant="outlined"
          value={quickFilterIsEmpty(activeValue) ? '' : String(activeValue)}
          onChange={(e) => onChange(e.target.value || '')}
          sx={{ bgcolor: 'white', borderRadius: 2, minWidth: isDT ? 220 : 160 }}
          InputLabelProps={{ shrink: true }}
        />
        {!quickFilterIsEmpty(activeValue) && (
          <Chip label={config.allLabel || "Tất cả"} clickable onClick={() => onChange('')} sx={chipSx(false)} />
        )}
      </Box>
    );
  }

  if (options.length === 0 && !loading) return null;

  // Điều kiện đếm cho một chip = điều kiện của các bộ lọc khác + chip này.
  const countCond = (value) => combineConditions([
    countBaseCondition,
    quickFilterCondition(config, config.multiSel ? [value] : value, (v) => quickFilterValue(v, context), options),
  ]);
  const showCount = !!config.showCount && !!countApiCode;
  const allActive = quickFilterIsEmpty(activeValue);

  return (
    <Box sx={rowSx}>
      {title}
      {/* NÚT "TẤT CẢ" — nhãn lấy theo `allLabel` (← `defaultLabel` của InputBase). Có NHIỀU hàng lọc
          nhanh cùng lúc (kho, trạng thái…) nên nhãn này là thứ duy nhất cho biết hàng đó lọc theo gì.
          `selectAll:false` thì không có chip này. */}
      {config.selectAll !== false && (
        <Chip
          label={config.allLabel || "Tất cả"}
          clickable
          onClick={() => onChange(config.multiSel ? [] : '')}
          sx={chipSx(allActive)}
        />
      )}

      {loading && <CircularProgress size={20} sx={{ ml: 1, color: IOS_BLUE }} />}

      {options.map(opt => {
        const isActive = quickFilterIsActive(config, activeValue, opt.value);
        return (
          <Chip
            key={String(opt.value)}
            label={<span>{opt.label}{showCount && <ChipCount apiCode={countApiCode} headers={countHeaders} condition={countCond(opt.value)} />}</span>}
            clickable
            variant={config.multiSel && !isActive ? 'outlined' : 'filled'}
            onClick={() => onChange(toggleQuickFilterValue(config, activeValue, opt.value))}
            sx={chipSx(isActive, opt.color)}
          />
        );
      })}
    </Box>
  );
}, (prevProps, nextProps) => {
  // ================= CƠ CHẾ CHẶN RE-RENDER VÔ LÝ =================
  // Chỉ vẽ lại khi giá trị đang chọn / cấu hình / điều kiện đếm đổi NỘI DUNG — miễn nhiễm với việc cha
  // dựng lại object mới mỗi lần vẽ. `prepareQuery`/`prepareData` là hàm (JSON bỏ qua) nên so riêng.
  // `onChange` KHÔNG so: cha truyền arrow mới mỗi lần vẽ, nhưng nó chỉ gọi setState dạng hàm theo
  // đúng `field` của hàng này nên bản cũ vẫn đúng.
  return JSON.stringify(prevProps.activeValue) === JSON.stringify(nextProps.activeValue) &&
         JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config) &&
         prevProps.config?.prepareQuery === nextProps.config?.prepareQuery &&
         prevProps.config?.prepareData === nextProps.config?.prepareData &&
         prevProps.countApiCode === nextProps.countApiCode &&
         JSON.stringify(prevProps.countBaseCondition) === JSON.stringify(nextProps.countBaseCondition);
});

export default DataListQuickFilter;
